import { DeleteOutlined } from '@ant-design/icons'
import { BBDTag, GraviButton, GraviGrid, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type { ColDef, GridApi } from 'ag-grid-community'
import { DatePicker, Form, InputNumber, message, Popconfirm, Popover, Switch } from 'antd'
import dayjs from 'dayjs'
import type * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useRateManagement } from '../../../../../../api/useFreight/useFreight'
import type { AutomaticAccessorial } from '../../../../../../api/useFreight/types'
import { RateStatusTag } from '../../components/RateStatusTag'
import { AutomaticAccessorialScheduleButton } from '../../components/ScheduleRateButton'
import type { BaseRate } from '../../components/ActiveStatusRenderer'

// Define the rate type based on the actual structure in AutomaticAccessorial
interface AutomaticAccessorialRate {
  id: string
  effective_from: string
  effective_to: string | null
  rate: number
  created_by: string
  created_on: string
  status: string
}

interface AutomaticAccessorialWithId extends AutomaticAccessorial {
  _id: string
  id: string
}

// Component for editing an existing rate
function EditRateButton({
  rate,
  accessorial,
  onSuccess,
}: {
  rate: AutomaticAccessorialRate
  accessorial: AutomaticAccessorialWithId
  onSuccess?: () => void
}) {
  const [form] = Form.useForm<{
    rate: number
    effective_date: dayjs.Dayjs
    effective_to?: dayjs.Dayjs
  }>()
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [showEffectiveTo, setShowEffectiveTo] = useState(!!rate.effective_to)
  const isPercentageType = accessorial.fee_type?.type?.includes('%') || false

  // Update switch state and form values when popover opens
  useEffect(() => {
    if (popoverVisible) {
      const hasEffectiveTo = !!rate.effective_to

      setShowEffectiveTo(hasEffectiveTo)

      form.setFieldsValue({
        effective_date: dayjs(rate.effective_from),
        effective_to: rate.effective_to ? dayjs(rate.effective_to) : undefined,
        rate: isPercentageType ? rate.rate * 100 : rate.rate,
      })
    }
  }, [popoverVisible, rate, form, isPercentageType])

  const { useUpdateAutomaticAccessorialRate } = useRateManagement()
  const updateRateMutation = useUpdateAutomaticAccessorialRate()

  const handleEffectiveToChange = (checked: boolean) => {
    setShowEffectiveTo(checked)
    if (!checked) {
      form.setFieldValue('effective_to', undefined)
    }
  }

  const handleEditRate = async (values: {
    rate: number
    effective_date: dayjs.Dayjs
    effective_to?: dayjs.Dayjs
  }) => {
    try {
      // Format the dates as ISO strings
      const effectiveFromDate = values.effective_date.format('YYYY-MM-DD')
      const effectiveToDate =
        showEffectiveTo && values.effective_to ? values.effective_to.format('YYYY-MM-DD') : undefined

      // Use the correct ID field
      const accessorialId = accessorial._id || accessorial.id

      // Convert percentage to decimal for API if it's a percentage type
      const apiRate = isPercentageType ? Number((values.rate / 100).toFixed(5)) : values.rate

      // Ensure we have a valid rate_id
      if (!rate.id) {
        message.error('Rate ID is missing. Cannot update rate.')
        return
      }

      await updateRateMutation.mutateAsync({
        accessorial_id: accessorialId,
        effective_from_date: effectiveFromDate,
        effective_to_date: effectiveToDate,
        rate: apiRate,
        rate_id: rate.id,
      })

      // Success notification
      message.success('Rate updated successfully')

      // Close popover
      setPopoverVisible(false)

      // Call onSuccess if provided
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('Failed to update rate:', error)
      message.error('Failed to update rate')
    }
  }

  return (
    <Popover
      content={
        <Form
          form={form}
          layout='vertical'
          onFinish={handleEditRate}
          initialValues={{
            effective_date: dayjs(rate.effective_from),
            effective_to: rate.effective_to ? dayjs(rate.effective_to) : undefined,
            rate: isPercentageType ? rate.rate * 100 : rate.rate,
          }}
        >
          <Form.Item
            name='rate'
            label='Rate'
            rules={[
              { required: true, message: 'Please enter rate' },
              {
                validator: (_, value) => {
                  if (isPercentageType && (value < 0 || value > 200)) {
                    return Promise.reject('Percentage must be between 0 and 200')
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <InputNumber
              prefix={isPercentageType ? null : '$'}
              suffix={isPercentageType ? '%' : null}
              precision={isPercentageType ? 3 : 5}
              max={isPercentageType ? 200 : undefined}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label='Effective From' style={{ marginBottom: 8 }}>
            <Horizontal justifyContent='space-between' alignItems='flex-end' gap={8}>
              <Form.Item
                name='effective_date'
                rules={[{ required: true, message: 'Please select effective date' }]}
                style={{ marginBottom: 0 }}
              >
                <DatePicker format='MM/DD/YYYY' />
              </Form.Item>
            </Horizontal>
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Horizontal verticalCenter gap={8}>
              <Switch checked={showEffectiveTo} onChange={handleEffectiveToChange} />
              <Texto>{showEffectiveTo ? 'End Date' : 'No End Date'}</Texto>
            </Horizontal>
          </Form.Item>

          {showEffectiveTo && (
            <Form.Item
              name='effective_to'
              label='Effective To'
              rules={[
                {
                  required: showEffectiveTo,
                  message: 'Please select end date',
                },
                {
                  validator: (_, value) => {
                    if (
                      showEffectiveTo &&
                      value &&
                      form.getFieldValue('effective_date') &&
                      value.isBefore(form.getFieldValue('effective_date'))
                    ) {
                      return Promise.reject('End date must be after start date')
                    }
                    return Promise.resolve()
                  },
                },
              ]}
              style={{ marginBottom: 16 }}
            >
              <DatePicker format='MM/DD/YYYY' />
            </Form.Item>
          )}

          <Horizontal gap={8} justifyContent='flex-end'>
            <GraviButton appearance='outlined' onClick={() => setPopoverVisible(false)}>
              Cancel
            </GraviButton>
            <GraviButton appearance='solid' theme2 htmlType='submit' loading={updateRateMutation.isPending}>
              Save
            </GraviButton>
          </Horizontal>
        </Form>
      }
      title='Edit Rate'
      trigger='click'
      placement='bottomLeft'
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
    >
      <GraviButton appearance='outlined' icon={<MaterialIcon icon='edit' />} />
    </Popover>
  )
}

// Component for deleting a scheduled rate
function DeleteRateButton({
  rate,
  accessorial,
  onSuccess,
}: {
  rate: AutomaticAccessorialRate
  accessorial: AutomaticAccessorialWithId
  onSuccess?: () => void
}) {
  const queryClient = useQueryClient()
  const { useDeleteAutomaticAccessorialRate } = useRateManagement()
  const deleteRateMutation = useDeleteAutomaticAccessorialRate()

  const handleDeleteRate = async () => {
    try {
      // Use the correct ID field
      const accessorialId = accessorial._id || ''

      await deleteRateMutation.mutateAsync({
        accessorial_id: accessorialId,
        effective_date: rate.effective_from,
      })

      // Directly invalidate queries
      await queryClient.invalidateQueries({
        queryKey: ['automatic_accessorials'],
      })
      message.success('Rate deleted successfully')

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error('Error deleting rate:', error)
      message.error('Failed to delete rate')
    }
  }

  return (
    <Popconfirm
      title='Delete Scheduled Rate'
      description='Are you sure you want to delete this scheduled rate?'
      onConfirm={handleDeleteRate}
      okText='Yes'
      cancelText='No'
      okButtonProps={{ danger: true }}
    >
      <GraviButton error icon={<DeleteOutlined />} />
    </Popconfirm>
  )
}

export interface RateHistoryDrawerProps {
  accessorial: AutomaticAccessorialWithId
  onRateChange?: () => void
}

export function RateHistoryDrawer({ accessorial, onRateChange }: RateHistoryDrawerProps) {
  const gridRef = useRef<GridApi<AutomaticAccessorialRate>>(null) as React.MutableRefObject<
    GridApi<AutomaticAccessorialRate>
  >
  const isPercentageType = accessorial.fee_type?.type?.includes('%') || false

  // Find the most recent active rate to display in header
  const mostRecentActiveRateId = useMemo(() => {
    // If accessorial is disabled, no rates should be active
    if (accessorial.disabled || !accessorial.rates?.length) return null

    const now = dayjs()

    // Filter for rates that are effective now (not scheduled)
    const currentRates = accessorial.rates.filter((r) => {
      const effectiveFrom = dayjs(r.effective_from)
      const effectiveTo = r.effective_to ? dayjs(r.effective_to) : null

      // A rate is current if:
      // 1. It started in the past or today
      const hasStarted = effectiveFrom.isBefore(now) || effectiveFrom.isSame(now, 'day')
      // 2. And either has no end date or hasn't ended yet
      const hasNotEnded = !effectiveTo || effectiveTo.isAfter(now)

      return hasStarted && hasNotEnded
    })

    // Sort by effective_from date in descending order to get most recent first
    const sortedRates = [...currentRates].sort((a, b) => {
      return dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf()
    })

    // Return the ID of the most recent active rate
    if (sortedRates.length === 0) return null

    const mostRecentRate = sortedRates[0]
    return mostRecentRate.id
  }, [accessorial.disabled, accessorial.rates])

  // Handle rate changes (deletion or editing)
  const handleRateChange = useCallback(() => {
    if (onRateChange) {
      onRateChange()
    }
  }, [onRateChange])

  // Ensure all rates have proper IDs and normalize the data structure
  const processedRateData = useMemo(() => {
    // First check if we have rates at all
    if (!accessorial) return []

    // Account for different possible locations of rates in the API response
    let ratesArray = accessorial.rates || accessorial.rate_history || []

    // For safety, if rates is available but not an array, convert to array
    if (ratesArray && !Array.isArray(ratesArray)) {
      console.warn('Rates property is not an array, converting:', ratesArray)
      ratesArray = [ratesArray]
    }

    // If no rates found, log a warning and return empty array
    if (!ratesArray.length) {
      console.warn('No rates found for accessorial:', accessorial)
      return []
    }

    // Map rates to ensure they have all required fields
    return ratesArray.map((rate) => ({
      ...rate,
      effective_to: rate.effective_to || null,
    })) as AutomaticAccessorialRate[]
  }, [accessorial])

  // Cell Renderers
  interface CellProps {
    value?: number | string | null
    data?: AutomaticAccessorialRate
  }

  const RateCell = useCallback(
    ({ value }: CellProps) => {
      if (value === null || value === undefined) {
        return <Texto appearance='hint'>N/A</Texto>
      }
      const numValue = typeof value === 'string' ? Number.parseFloat(value) : value
      const formattedValue = isPercentageType ? `${(numValue * 100).toFixed(3)}%` : `$${numValue.toFixed(5)}`
      return <BBDTag theme2>{formattedValue}</BBDTag>
    },
    [isPercentageType]
  )

  const StatusCell = useCallback(
    ({ data }: { data: AutomaticAccessorialRate }) => {
      if (!data) return null
      const isActive = !accessorial.disabled && data.id === mostRecentActiveRateId
      return <RateStatusTag rate={data} isMostRecentActive={isActive} />
    },
    [accessorial.disabled, mostRecentActiveRateId]
  )

  const FromDateCell = useCallback(({ value }: CellProps) => {
    return <Texto>{dayjs(value).format('MM/DD/YY')}</Texto>
  }, [])

  const ToDateCell = useCallback(({ data }: CellProps) => {
    if (!data) return null
    const toDate = data.effective_to
    return toDate ? <Texto>{dayjs(toDate).format('MM/DD/YY')}</Texto> : <Texto appearance='hint'>Indefinite</Texto>
  }, [])

  const ActionsCell = useCallback(
    ({ data }: CellProps) => {
      if (!data) return null

      const now = dayjs()
      const effectiveFrom = dayjs(data.effective_from)
      const isScheduled = effectiveFrom.isAfter(now)

      return (
        <Horizontal gap={8} verticalCenter style={{ overflow: 'visible', width: '100%' }}>
          <EditRateButton rate={data} accessorial={accessorial} onSuccess={handleRateChange} />
          {isScheduled && <DeleteRateButton rate={data} accessorial={accessorial} onSuccess={handleRateChange} />}
        </Horizontal>
      )
    },
    [accessorial, handleRateChange]
  )

  // Column definitions for the GraviGrid
  const historyColumnDefs = useMemo<ColDef<AutomaticAccessorialRate>[]>(
    () => [
      {
        headerName: 'Rate',
        field: 'rate',
        minWidth: 100,
        cellStyle: () => ({ textAlign: 'right' }),
        type: 'numericColumn',
        cellRenderer: RateCell,
      },
      {
        headerName: 'Status',
        minWidth: 120,
        cellRenderer: StatusCell,
      },
      {
        headerName: 'From',
        field: 'effective_from',
        minWidth: 100,
        cellRenderer: FromDateCell,
      },
      {
        headerName: 'To',
        field: 'effective_to',
        minWidth: 100,
        cellRenderer: ToDateCell,
      },
      {
        headerName: '',
        minWidth: 80,
        width: 80,
        pinned: 'right',
        cellStyle: () => ({
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }),
        cellRenderer: ActionsCell,
      },
    ],
    [RateCell, StatusCell, FromDateCell, ToDateCell, ActionsCell]
  )

  // Hook to refresh grid when accessorial or its rates change
  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.setRowData(processedRateData)
      gridRef.current.sizeColumnsToFit()
      gridRef.current.refreshCells({ force: true })
    }
  }, [processedRateData, gridRef.current])

  return (
    <Vertical flex={1}>
      <Vertical
        flex='0'
        style={{ overflow: 'visible' }}
        border='all'
        borderRadius='4px'
        bg='elevated'
        className='p-3 mb-3'
        gap={12}
      >
        <Texto category='h5' weight='600'>
          {accessorial.name}
        </Texto>

        <Horizontal justifyContent='space-between'>
          <Horizontal gap={24}>
            <Vertical>
              <Texto appearance='hint'>Product Group</Texto>
              <BBDTag theme2>{accessorial.product_group || 'Any'}</BBDTag>
            </Vertical>
          </Horizontal>
          <Horizontal verticalCenter gap={8}>
            <AutomaticAccessorialScheduleButton
              accessorial={accessorial}
              onScheduleSuccess={onRateChange}
              initialRate={
                isPercentageType && accessorial.rates?.length
                  ? accessorial.rates[0].rate * 100
                  : accessorial.rates?.[0]?.rate
              }
            />
          </Horizontal>
        </Horizontal>
      </Vertical>

      <Vertical flex={1} style={{ minHeight: '300px' }}>
        <GraviGrid
          key={`rate-history-${accessorial._id || accessorial.id}-${processedRateData.length}`}
          storageKey='automatic_accessorial_rate_history'
          rowData={processedRateData}
          columnDefs={historyColumnDefs}
          agPropOverrides={{
            getRowId: (params) => params.data.id,
            rowGroupPanelShow: 'never',
            rowHeight: 40,
          }}
          controlBarProps={{
            title: `Rate History (${processedRateData.length})`,
            customSearchBar: <div />,
          }}
          externalRef={gridRef}
        />
        {processedRateData.length === 0 && (
          <Vertical
            flex={1}
            justifyContent='center'
            alignItems='center'
            gap={8}
            style={{ height: '200px', marginTop: '16px' }}
          >
            <MaterialIcon icon='info' size={32} color='var(--text-hint)' />
            <Texto appearance='hint'>No rate history found</Texto>
          </Vertical>
        )}
      </Vertical>
    </Vertical>
  )
}
